"use client";

import { Check, HelpCircle, Mountain, TrendingUp, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect } from "react";
export default function TierComparisonTable() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  useEffect(() => {
    const calendlyCssHref = "https://assets.calendly.com/assets/external/widget.css";
    const calendlyScriptSrc = "https://assets.calendly.com/assets/external/widget.js";
    if (!document.querySelector(`link[href="${calendlyCssHref}"]`)) {
      const link = document.createElement("link");
      link.href = calendlyCssHref;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    if (!document.querySelector(`script[src="${calendlyScriptSrc}"]`)) {
      const script = document.createElement("script");
      script.src = calendlyScriptSrc;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);
  const handleScheduleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (typeof window !== "undefined" && (window as any).Calendly) {
      (window as any).Calendly.initPopupWidget({
        url: "https://calendly.com/mfloresdo/30min"
      });
    }
  };
  const packages = [{
    icon: Mountain,
    name: "Basecamp",
    price: "$150/month",
    label: "Recommended starting point",
    bestFor: "Men who want physician-led TRT care, clear guidance, and a focused starting point.",
    chooseIf: ["You mainly want to evaluate and treat low testosterone when appropriate", "You want direct physician oversight without a large monthly commitment", "You are comfortable keeping the plan focused on TRT care first"],
    note: "Best first step for most qualified men who are starting with TRT.",
    highlight: true
  }, {
    icon: TrendingUp,
    name: "Ascent",
    price: "$325/month",
    label: "Most common upgrade",
    bestFor: "Men who want TRT care plus included labs and additional support for recovery, metabolism, and wellness.",
    chooseIf: ["You want labs included in your monthly package", "You may benefit from NAD+ or vitamin injections when clinically indicated", "You want supplement recommendations and broader metabolic support", "You may want GLP-1 therapy if appropriate, billed separately"],
    note: "Good fit for men who want more support than TRT alone.",
    highlight: false
  }, {
    icon: Crown,
    name: "Summit",
    price: "$595/month",
    label: "Highest-touch support",
    bestFor: "Men who want the most comprehensive physician-led support, advanced monitoring, and closer follow-up.",
    chooseIf: ["You want included labs with a more advanced cardiometabolic review", "You have more complex goals or health markers", "You want higher-touch support and closer physician-led follow-up", "You want the broadest performance, metabolic, and longevity strategy"],
    note: "Best for men who want the most complete level of oversight.",
    highlight: false
  }];
  return <section className="py-12 md:py-16 bg-background overflow-hidden">
      <div className="container px-4 sm:px-6 lg:px-8">
        <Card ref={ref} className={`transition-all duration-700 shadow-soft border border-muted-light overflow-hidden ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <CardHeader className="text-center px-5 sm:px-6 md:px-8 pt-8">
            <div className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/30 bg-primary-light-bg px-4 py-2 mb-5 mx-auto">
              <HelpCircle className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">
                Not Sure Where to Start?
              </span>
            </div>

            <CardTitle className="text-2xl md:text-3xl lg:text-4xl text-foreground leading-tight">
              Which Package Is Right for You?
            </CardTitle>

            <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed mt-4">
              Most men start with Basecamp TRT. Ascent and Summit are designed
              for men who need included labs, additional recovery support,
              metabolic support, or a higher-touch level of care.
            </p>
          </CardHeader>

          <CardContent className="px-5 sm:px-6 md:px-8 pb-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {packages.map((pkg, i) => {
              const IconComponent = pkg.icon;
              return <div key={pkg.name} data-index={i} className={`rounded-2xl border p-5 md:p-6 min-w-0 transition-all duration-300 ${pkg.highlight ? "border-primary/40 bg-primary-light-bg shadow-medium" : "border-muted-light bg-card shadow-soft"} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
                transitionDelay: `${i * 120}ms`
              }}>
                    <div className="flex items-start justify-between gap-4 mb-5">
                      <div className="min-w-0">
                        <div className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">
                          {pkg.label}
                        </div>

                        <h3 className="text-2xl font-bold text-foreground">
                          {pkg.name}
                        </h3>

                        <p className="text-sm font-semibold text-muted-foreground mt-1">
                          {pkg.price}
                        </p>
                      </div>

                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${pkg.highlight ? "bg-card" : "bg-primary-light-bg"}`}>
                        <IconComponent className="w-6 h-6 text-primary" />
                      </div>
                    </div>

                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-5">
                      {pkg.bestFor}
                    </p>

                    <div className="border-t border-muted-light pt-5">
                      <h4 className="font-semibold text-foreground mb-3">
                        Choose this if:
                      </h4>

                      <ul className="space-y-3">
                        {pkg.chooseIf.map((item, itemIndex) => <li key={itemIndex} className="flex items-start gap-3 text-sm text-muted-foreground leading-relaxed">
                            <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>

                    <div className="mt-5 rounded-lg bg-background/70 border border-muted-light p-4">
                      <p className="text-xs text-muted-foreground leading-relaxed italic">
                        {pkg.note}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            <div className="mt-8 rounded-xl border border-muted-light bg-background p-5 md:p-6">
              <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-4 text-center">
                Important Pricing Notes
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-foreground mb-3">
                    Included in Ascent and Summit
                  </h4>

                  <ul className="space-y-2 text-sm text-muted-foreground leading-relaxed">
                    <li className="flex gap-2">
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span>Lab testing included</span>
                    </li>
                    <li className="flex gap-2">
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span>NAD+ therapy when clinically indicated</span>
                    </li>
                    <li className="flex gap-2">
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span>Vitamin injections when clinically indicated</span>
                    </li>
                    <li className="flex gap-2">
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span>Supplement recommendations</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-foreground mb-3">
                    Billed Separately When Used
                  </h4>

                  <ul className="space-y-2 text-sm text-muted-foreground leading-relaxed">
                    <li className="flex gap-2">
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span>
                        GLP-1 medications, available only in Ascent or Summit
                        when medically appropriate
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span>Supplements you choose to purchase</span>
                    </li>
                    <li className="flex gap-2">
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span>
                        Services, medications, or testing outside the selected
                        package
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <p className="text-xs text-muted-foreground italic text-center mt-6 max-w-4xl mx-auto leading-relaxed">
                IronPeak Men’s Health does not accept insurance. Treatment is
                prescribed only when medically appropriate after physician
                review.
              </p>
            </div>

            <div className="mt-8 flex justify-center">
              <Button onClick={handleScheduleClick} size="lg" className="w-full max-w-[340px] sm:w-auto sm:max-w-none inline-flex items-center justify-center gap-2 whitespace-normal text-center leading-snug text-sm lg:text-base font-semibold rounded-sm bg-accent text-accent-foreground hover:bg-accent-hover hover:scale-105 active:bg-accent-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 shadow-soft hover:shadow-medium transition-all duration-200 ease-out px-6 lg:px-8 py-3">
                Book Free Consultation
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>;
}